import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { ChartLine, DownloadSimple } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { inr } from "@/lib/constants";
import { PageHeader, SectionTitle, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function BillingReports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/billing").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === false) return <EmptyState icon={ChartLine}>Could not load billing reports.</EmptyState>;
  if (!data) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Building billing reports…</div>;

  const invoiced = data.monthly.reduce((s, m) => s + m.invoiced, 0);
  const collected = data.monthly.reduce((s, m) => s + m.collected, 0);

  return (
    <div className="space-y-6" data-testid="billing-reports-page">
      <PageHeader
        overline="Invoices" title="Billing Reports"
        subtitle="Rollups for accounting and management review"
        action={<Button asChild variant="outline" className="rounded-full"><a href={`${API}/export/invoices`} data-testid="export-invoices-btn"><DownloadSimple size={16} className="mr-1" /> Export Invoices CSV</a></Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total invoiced" value={inr(invoiced)} testid="stat-invoiced" />
        <Stat label="Total collected" value={inr(collected)} tone="text-emerald-600" testid="stat-collected" />
        <Stat label="Uncollected" value={inr(invoiced - collected)} tone="text-amber-600" testid="stat-uncollected" />
        <Stat label="Months billed" value={data.monthly.length} testid="stat-months" />
      </div>

      <Card className="p-6 border-border">
        <SectionTitle icon={ChartLine}>Monthly Billing Summary</SectionTitle>
        {data.monthly.length === 0 ? <EmptyState>No invoices raised yet.</EmptyState> : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly} margin={{ left: 10, right: 10 }}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="invoiced" stroke="#0A2540" strokeWidth={2} dot={{ r: 3 }} name="Invoiced" />
              <Line type="monotone" dataKey="collected" stroke="#B8860B" strokeWidth={2} dot={{ r: 3 }} name="Collected" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border">
          <SectionTitle>Revenue by Bank</SectionTitle>
          {data.by_bank.length === 0 ? <EmptyState>No jobs yet.</EmptyState> : (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_bank.length * 34)}>
              <BarChart data={data.by_bank} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="fees" fill="#0A2540" radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-display text-lg font-semibold text-primary">Revenue by Valuer</h3>
            <p className="text-xs text-muted-foreground">Fee split across field executive and report writer</p>
          </div>
          {data.by_valuer.length === 0 ? <div className="p-6"><EmptyState>No jobs assigned yet.</EmptyState></div> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wide">Team Member</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Jobs</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Attributed Fees</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_valuer.map((v) => (
                    <TableRow key={v.name} className="hover:bg-muted/40" data-testid={`valuer-rev-${v.name}`}>
                      <TableCell className="text-sm font-medium text-primary">{v.name}</TableCell>
                      <TableCell className="text-right tabular text-sm">{v.jobs}</TableCell>
                      <TableCell className="text-right tabular text-sm">{inr(v.fees)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
